import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../Button';
import { Input } from '../Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { qrApi } from '../../api/services/qr.service';
import { subscriptionService } from '../../api/services';
import { analytics } from '../../services/analytics';

interface CreateQRCodeModalProps {
  open: boolean;
  onClose: () => void;
}

function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

function isValidUrl(url: string): boolean {
  if (!url.trim()) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(normalizeUrl(url));
    return true;
  } catch {
    return false;
  }
}

export function CreateQRCodeModal({ open, onClose }: CreateQRCodeModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);

  const { data: usage } = useQuery({
    queryKey: ['usage-stats'],
    queryFn: subscriptionService.getUsageStats,
    enabled: open,
  });
  const canCreate = usage?.can_create_qr ?? true;

  const createMutation = useMutation({
    mutationFn: (landingSection: 'top' | 'smart-routing') =>
      qrApi.createQRCode({
        fallback_url: normalizeUrl(url),
        title: name.trim() || undefined,
      } as any).then((created) => ({ created, landingSection })),
    onSuccess: ({ created, landingSection }) => {
      analytics.capture('qr_created', { qr_id: created.id });
      toast.success('QR code created!');
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      queryClient.invalidateQueries({ queryKey: ['qr-codes-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['usage-stats'] });
      reset();
      onClose();
      navigate(`/dashboard/qr-codes/${created.id}/edit${landingSection === 'smart-routing' ? '#smart-routing' : ''}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to create QR code');
    },
  });

  const reset = () => {
    setUrl('');
    setName('');
    setTouched(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const urlValid = isValidUrl(url);

  const handleCreate = (landingSection: 'top' | 'smart-routing') => {
    setTouched(true);
    if (!urlValid || !canCreate || createMutation.isPending) return;
    createMutation.mutate(landingSection);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="bg-white border border-[--clinqr-border] rounded-lg shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[--clinqr-text-primary]">Create QR Code</DialogTitle>
        </DialogHeader>

        {!canCreate ? (
          <div className="py-4 space-y-4">
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                You've reached your QR code limit ({usage?.qr_code_count}/{usage?.qr_code_limit}). Upgrade your
                plan to create more.
              </p>
            </div>
            <Button
              variant="primary"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => {
                handleClose();
                navigate('/dashboard/billing');
              }}
            >
              <Zap className="w-4 h-4" />
              View Pricing Plans
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <Input
                label="Destination URL"
                placeholder="example.com/page"
                autoFocus
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                error={touched && !urlValid ? 'Please enter a valid URL' : undefined}
              />
              <Input
                label="Name (optional)"
                placeholder="Auto-generated from URL"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                disabled={createMutation.isPending}
                onClick={() => handleCreate('smart-routing')}
              >
                Create & Customize
              </Button>
              <Button
                variant="primary"
                className="w-full sm:w-auto !bg-[--clinqr-lime] !text-[--clinqr-navy] hover:brightness-95 border border-[--clinqr-navy]/20"
                disabled={createMutation.isPending}
                onClick={() => handleCreate('top')}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
